/**
 * Escena del CAMPO DE FÚTBOL CASA (canon Adri 2026-07-11, ver
 * `docs/biblia/escenarios.md`). Pista de tierra entre árboles con el
 * tronco de red, columpio y aro atado al árbol como decoración dibujada.
 *
 * Solo expone el minijuego estrella de la zona: la **pachanga 2v2**
 * (`mg-pachanga`, fútbol casa con el tronco de red). El resto (columpio,
 * aro del árbol) es ambient visual, no interactivo en la beta.
 *
 * Separación dura: textos en `src/data/zonas-actividad.ts`. Esta escena
 * solo dibuja y enruta.
 */

import Phaser from 'phaser';
import {
  ensureRunning,
  goTo,
  SCENE_KEYS,
  type SceneKey,
} from '@/engine/nav';
import {
  BOTON_SUBIR_CASA,
  BOTON_VOLVER_BARRIO,
  DESCRIPCION_CAMPO_FUTBOL,
} from '@/data/zonas-actividad';
import { getMinigame } from '@/data/minijuegos';
import { LaCasaScene } from './LaCasaScene';

// Paleta coherente con el resto de escenas (pórtico tarde, suelo tierra).
const COLOR_FONDO_CIELO = 0xa3c4e8; // azul tarde
const COLOR_CIELO_ALTO = 0xd2b48c; // banda más cálida cerca del horizonte
const COLOR_SUELO = 0x6b4a2b; // tierra pisada
const COLOR_LINEA_CAMPO = 0xffffff;
const COLOR_TRONCO = 0x5a3a1f;
const COLOR_TRONCO_OSCURO = 0x3b2412;
const COLOR_PINO = 0x2e5a32;
const COLOR_PINO_TRONCO = 0x3b2412;
const COLOR_HOJA_CLARA = 0x9aa84a;
const COLOR_CUERDA_COLUMPIO = 0x222230;
const COLOR_ASIENTO_COLUMPIO = 0x6b4423;
const COLOR_ARO = 0xff7f24;
const COLOR_TABLERO = 0xffffff;
const COLOR_PANEL = 0x22222e;
const COLOR_BOTON = 0x2a2a38;
const COLOR_BOTON_HOVER = 0x38384a;
const COLOR_BOTON_DESTACADO = 0xc0392b;
const COLOR_BOTON_DESTACADO_HOVER = 0xe74c3c;
const COLOR_BORDE_PANEL = 0x38384a;
const COLOR_TEXTO = '#ffffff';
const COLOR_TEXTO_DIM = '#8a8a95';
const COLOR_TEXTO_DESTACADO = '#fff8e1';

const HUD_ALTO = 100;
const MARGEN = 24;

export class CampoFutbolScene extends Phaser.Scene {
  /** Key pública — el orquestador la registra en nav.ts. */
  public static readonly KEY = 'CampoFutbolScene';

  private ambienteContainer!: Phaser.GameObjects.Container;
  private uiContainer!: Phaser.GameObjects.Container;

  constructor() {
    super(CampoFutbolScene.KEY);
  }

  create(): void {
    const { width, height } = this.scale;
    this.cameras.main.setBackgroundColor(COLOR_FONDO_CIELO);

    ensureRunning(this, SCENE_KEYS.HUD);
    this.scene.bringToTop(SCENE_KEYS.HUD);

    this.ambienteContainer = this.add.container(0, 0);
    this.uiContainer = this.add.container(0, 0);

    this.dibujarAmbiente(width, height);
    this.renderUI(width, height);
  }

  // ---------------------------------------------------------------------
  // Ambiente: pista de tierra con tronco-red, pinos, columpio y aro
  // ---------------------------------------------------------------------

  private dibujarAmbiente(width: number, height: number): void {
    // Cielo en dos bandas (más cálida abajo, sugiriendo media tarde).
    const cielo = this.add.graphics();
    cielo.fillStyle(COLOR_FONDO_CIELO, 1);
    cielo.fillRect(0, 0, width, height * 0.55);
    cielo.fillStyle(COLOR_CIELO_ALTO, 0.85);
    cielo.fillRect(0, height * 0.4, width, height * 0.18);
    this.ambienteContainer.add(cielo);

    // Pinos al fondo formando el "bosque" entre el que está la pista.
    const pinos = this.add.graphics();
    pinos.fillStyle(COLOR_PINO, 1);
    for (let i = 0; i < 14; i++) {
      const px = (width / 14) * i + 6;
      const py = height * 0.32 + (i % 2 === 0 ? 0 : 12);
      pinos.fillTriangle(px, py, px + 18, py + 90, px + 36, py);
    }
    this.ambienteContainer.add(pinos);

    // Suelo de la pista (tierra pisada).
    const suelo = this.add.graphics();
    suelo.fillStyle(COLOR_SUELO, 1);
    suelo.fillRect(0, height * 0.55, width, height * 0.45);
    this.ambienteContainer.add(suelo);

    // Hierba seca en los bordes superior e inferior de la pista.
    const cespedTop = this.add.graphics();
    cespedTop.fillStyle(COLOR_HOJA_CLARA, 0.6);
    cespedTop.fillRect(0, height * 0.53, width, 8);
    this.ambienteContainer.add(cespedTop);

    // Tronco de red (en horizontal en el centro de la pista).
    this.dibujarTroncoRed(width / 2, height * 0.66, width * 0.6);

    // Marcas de campo medio borradas (línea horizontal discontinua).
    const marcas = this.add.graphics();
    marcas.lineStyle(2, COLOR_LINEA_CAMPO, 0.25);
    marcas.beginPath();
    marcas.moveTo(0, height * 0.66 + 30);
    marcas.lineTo(width, height * 0.66 + 30);
    marcas.strokePath();
    this.ambienteContainer.add(marcas);

    // Árbol con columpio a la izquierda de la pista.
    this.dibujarArbolConColumpio(width * 0.16, height * 0.55);

    // Árbol con aro de basket atado a la derecha.
    this.dibujarArbolConAro(width * 0.86, height * 0.55);

    // Pelota de fútbol en primer plano como ambient (no interactiva).
    this.ambienteContainer.add(
      this.add
        .text(width * 0.5, height * 0.92, '⚽', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '28px',
        })
        .setOrigin(0.5),
    );
  }

  private dibujarTroncoRed(cx: number, cy: number, ancho: number): void {
    // Sombra del tronco.
    const sombra = this.add.graphics();
    sombra.fillStyle(0x000000, 0.25);
    sombra.fillRect(cx - ancho / 2, cy + 8, ancho, 18);
    this.ambienteContainer.add(sombra);

    // Tronco (rectángulo redondeado horizontal).
    const tronco = this.add.graphics();
    tronco.fillStyle(COLOR_TRONCO, 1);
    tronco.fillRoundedRect(cx - ancho / 2, cy, ancho, 16, 6);
    tronco.lineStyle(2, COLOR_TRONCO_OSCURO, 1);
    tronco.strokeRoundedRect(cx - ancho / 2, cy, ancho, 16, 6);
    // Anillos en el tronco (decoración).
    tronco.lineStyle(1, COLOR_TRONCO_OSCURO, 0.6);
    for (let i = 0; i < 6; i++) {
      const rx = cx - ancho / 2 + (ancho / 6) * i + ancho / 12;
      tronco.beginPath();
      tronco.moveTo(rx, cy + 2);
      tronco.lineTo(rx, cy + 14);
      tronco.strokePath();
    }
    this.ambienteContainer.add(tronco);

    // Etiqueta "RED" pequeña encima del tronco.
    this.ambienteContainer.add(
      this.add
        .text(cx, cy - 10, '↔ RED (tronco) ↔', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '13px',
          color: '#ffffff',
        })
        .setOrigin(0.5),
    );
  }

  private dibujarArbolConColumpio(cx: number, cy: number): void {
    // Tronco del árbol.
    const tronco = this.add.graphics();
    tronco.fillStyle(COLOR_PINO_TRONCO, 1);
    tronco.fillRect(cx - 4, cy, 8, 160);
    this.ambienteContainer.add(tronco);

    // Copa (pino estilizado).
    const copa = this.add.graphics();
    copa.fillStyle(COLOR_PINO, 1);
    copa.fillTriangle(cx - 28, cy + 20, cx + 28, cy + 20, cx, cy - 30);
    copa.fillTriangle(cx - 22, cy, cx + 22, cy, cx, cy - 60);
    this.ambienteContainer.add(copa);

    // Rama de la que cuelga el columpio.
    const rama = this.add.graphics();
    rama.fillStyle(COLOR_PINO_TRONCO, 1);
    rama.fillRect(cx - 30, cy + 8, 50, 4);
    this.ambienteContainer.add(rama);

    // Cuerdas del columpio (dos líneas).
    const cuerdas = this.add.graphics();
    cuerdas.lineStyle(2, COLOR_CUERDA_COLUMPIO, 1);
    cuerdas.beginPath();
    cuerdas.moveTo(cx + 2, cy + 12);
    cuerdas.lineTo(cx - 6, cy + 70);
    cuerdas.strokePath();
    cuerdas.beginPath();
    cuerdas.moveTo(cx + 18, cy + 12);
    cuerdas.lineTo(cx + 26, cy + 70);
    cuerdas.strokePath();
    this.ambienteContainer.add(cuerdas);

    // Asiento del columpio (rectángulo marrón).
    const asiento = this.add.graphics();
    asiento.fillStyle(COLOR_ASIENTO_COLUMPIO, 1);
    asiento.fillRect(cx - 8, cy + 70, 36, 8);
    this.ambienteContainer.add(asiento);
  }

  private dibujarArbolConAro(cx: number, cy: number): void {
    // Tronco del árbol.
    const tronco = this.add.graphics();
    tronco.fillStyle(COLOR_PINO_TRONCO, 1);
    tronco.fillRect(cx - 4, cy, 8, 160);
    this.ambienteContainer.add(tronco);

    // Copa del pino.
    const copa = this.add.graphics();
    copa.fillStyle(COLOR_PINO, 1);
    copa.fillTriangle(cx - 28, cy + 20, cx + 28, cy + 20, cx, cy - 30);
    copa.fillTriangle(cx - 22, cy, cx + 22, cy, cx, cy - 60);
    this.ambienteContainer.add(copa);

    // Tablero del aro atado al árbol (clavado en el tronco).
    const tablero = this.add.graphics();
    tablero.fillStyle(COLOR_TABLERO, 1);
    tablero.fillRect(cx + 16, cy + 30, 50, 40);
    tablero.lineStyle(2, COLOR_BORDE_PANEL, 1);
    tablero.strokeRect(cx + 16, cy + 30, 50, 40);
    this.ambienteContainer.add(tablero);

    // Aro naranja.
    this.ambienteContainer.add(
      this.add.circle(cx + 41, cy + 58, 10, COLOR_ARO).setStrokeStyle(2, 0xffffff, 1),
    );

    // Cuerda que lo ata al árbol (decorativa).
    const cuerda = this.add.graphics();
    cuerda.lineStyle(2, COLOR_CUERDA_COLUMPIO, 1);
    cuerda.beginPath();
    cuerda.moveTo(cx + 16, cy + 50);
    cuerda.lineTo(cx - 4, cy + 30);
    cuerda.strokePath();
    this.ambienteContainer.add(cuerda);
  }

  // ---------------------------------------------------------------------
  // UI: panel ambient + 2 botones (pachanga + subir a la casa) + volver barrio
  // ---------------------------------------------------------------------

  private renderUI(width: number, height: number): void {
    this.limpiarUI();

    this.renderPanelAmbient(width);
    this.renderBotones(width, height);
    this.renderBotonVolver(width, height);
  }

  private limpiarUI(): void {
    if (!this.uiContainer) return;
    this.uiContainer.removeAll(true);
  }

  private renderPanelAmbient(width: number): void {
    const panelAncho = width - MARGEN * 2;
    const panelAlto = 120;
    const y = HUD_ALTO + 70;

    const panel = this.add.graphics();
    panel.fillStyle(COLOR_PANEL, 0.92);
    panel.fillRoundedRect(-panelAncho / 2, -panelAlto / 2, panelAncho, panelAlto, 18);
    panel.lineStyle(2, COLOR_BORDE_PANEL, 1);
    panel.strokeRoundedRect(-panelAncho / 2, -panelAlto / 2, panelAncho, panelAlto, 18);
    panel.setPosition(width / 2, y);
    this.uiContainer.add(panel);

    this.uiContainer.add(
      this.add
        .text(width / 2 - panelAncho / 2 + 50, y - 18, DESCRIPCION_CAMPO_FUTBOL.emoji, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '34px',
        })
        .setOrigin(0.5),
    );
    this.uiContainer.add(
      this.add.text(
        width / 2 - panelAncho / 2 + 96,
        y - 22,
        DESCRIPCION_CAMPO_FUTBOL.nombre,
        {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '18px',
          fontStyle: 'bold',
          color: COLOR_TEXTO_DESTACADO,
        },
      ),
    );

    this.uiContainer.add(
      this.add.text(width / 2 - 8, y + 16, DESCRIPCION_CAMPO_FUTBOL.ambient, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '12px',
        color: COLOR_TEXTO,
        align: 'left',
        wordWrap: { width: panelAncho - 150 },
      }),
    );
  }

  private renderBotones(width: number, height: number): void {
    // 2 botones: pachanga (destacado, grande) + subir a la casa.
    const areaTop = HUD_ALTO + 220;
    const areaBottom = height - 110;
    const ancho = width - MARGEN * 2;
    const gap = 14;
    const botonAltoPachanga = (areaBottom - areaTop - gap) * 0.62;
    const botonAltoVolver = areaBottom - areaTop - gap - botonAltoPachanga;
    const x = width / 2;

    const mgPachanga = getMinigame('mg-pachanga');

    // Botón grande de la pachanga.
    this.crearBotonDestacado(
      {
        emoji: '⚽',
        titulo: 'Pachanga 2v2',
        subtitulo: mgPachanga.mecanica,
        alPulsar: () =>
          goTo(this, SCENE_KEYS.MG_PACHANGA, { volverA: CampoFutbolScene.KEY }),
      },
      x,
      areaTop + botonAltoPachanga / 2,
      ancho,
      botonAltoPachanga,
    );

    // Botón "Subir a la casa".
    this.crearBoton(
      {
        emoji: '🏠',
        titulo: BOTON_SUBIR_CASA,
        subtitulo: 'Volver al terreno de la caseta.',
        alPulsar: () => goTo(this, LaCasaScene.KEY as SceneKey),
      },
      x,
      areaTop + botonAltoPachanga + gap + botonAltoVolver / 2,
      ancho,
      botonAltoVolver,
    );
  }

  private crearBotonDestacado(
    spec: { emoji: string; titulo: string; subtitulo: string; alPulsar: () => void },
    x: number,
    y: number,
    ancho: number,
    alto: number,
  ): void {
    const boton = this.add.graphics();
    const dibujar = (color: number) => {
      boton.clear();
      boton.fillStyle(color, 1);
      boton.fillRoundedRect(-ancho / 2, -alto / 2, ancho, alto, 16);
    };
    dibujar(COLOR_BOTON_DESTACADO);
    boton.setPosition(x, y);
    this.uiContainer.add(boton);

    this.uiContainer.add(
      this.add
        .text(x - ancho / 2 + 32, y - 8, spec.emoji, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '34px',
        })
        .setOrigin(0, 0.5),
    );
    this.uiContainer.add(
      this.add.text(x - ancho / 2 + 88, y - 12, spec.titulo, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '22px',
        fontStyle: 'bold',
        color: COLOR_TEXTO_DESTACADO,
      }),
    );
    this.uiContainer.add(
      this.add.text(x - ancho / 2 + 88, y + 18, spec.subtitulo, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '14px',
        color: '#fff8e1',
        wordWrap: { width: ancho - 110 },
      }),
    );

    const zona = this.add.zone(x, y, ancho, alto).setInteractive({ useHandCursor: true });
    zona.on('pointerover', () => dibujar(COLOR_BOTON_DESTACADO_HOVER));
    zona.on('pointerout', () => dibujar(COLOR_BOTON_DESTACADO));
    zona.on('pointerdown', () => dibujar(COLOR_BOTON_DESTACADO_HOVER));
    zona.on('pointerup', () => spec.alPulsar());
    this.uiContainer.add(zona);
  }

  private crearBoton(
    spec: { emoji: string; titulo: string; subtitulo: string; alPulsar: () => void },
    x: number,
    y: number,
    ancho: number,
    alto: number,
  ): void {
    const boton = this.add.graphics();
    const dibujar = (color: number) => {
      boton.clear();
      boton.fillStyle(color, 1);
      boton.fillRoundedRect(-ancho / 2, -alto / 2, ancho, alto, 14);
    };
    dibujar(COLOR_BOTON);
    boton.setPosition(x, y);
    this.uiContainer.add(boton);

    this.uiContainer.add(
      this.add
        .text(x - ancho / 2 + 28, y - 8, spec.emoji, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '28px',
        })
        .setOrigin(0, 0.5),
    );
    this.uiContainer.add(
      this.add.text(x - ancho / 2 + 70, y - 10, spec.titulo, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '18px',
        fontStyle: 'bold',
        color: COLOR_TEXTO,
      }),
    );
    this.uiContainer.add(
      this.add.text(x - ancho / 2 + 70, y + 14, spec.subtitulo, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '13px',
        color: COLOR_TEXTO_DIM,
        wordWrap: { width: ancho - 90 },
      }),
    );

    const zona = this.add.zone(x, y, ancho, alto).setInteractive({ useHandCursor: true });
    zona.on('pointerover', () => dibujar(COLOR_BOTON_HOVER));
    zona.on('pointerout', () => dibujar(COLOR_BOTON));
    zona.on('pointerdown', () => dibujar(COLOR_BOTON_HOVER));
    zona.on('pointerup', () => spec.alPulsar());
    this.uiContainer.add(zona);
  }

  private renderBotonVolver(width: number, height: number): void {
    const ancho = 260;
    const alto = 56;
    const y = height - 30;

    const boton = this.add.graphics();
    const dibujar = (color: number) => {
      boton.clear();
      boton.fillStyle(color, 1);
      boton.fillRoundedRect(-ancho / 2, -alto / 2, ancho, alto, 14);
    };
    dibujar(COLOR_BOTON);
    boton.setPosition(width / 2, y);
    this.uiContainer.add(boton);

    this.uiContainer.add(
      this.add
        .text(width / 2, y, BOTON_VOLVER_BARRIO, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '18px',
          color: COLOR_TEXTO,
        })
        .setOrigin(0.5),
    );

    const zona = this.add.zone(width / 2, y, ancho, alto).setInteractive({ useHandCursor: true });
    zona.on('pointerover', () => dibujar(COLOR_BOTON_HOVER));
    zona.on('pointerout', () => dibujar(COLOR_BOTON));
    zona.on('pointerdown', () => dibujar(COLOR_BOTON_HOVER));
    zona.on('pointerup', () => {
      goTo(this, SCENE_KEYS.HUB);
    });
    this.uiContainer.add(zona);
  }
}
